import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GlassCard, CardHeader, Button, Icon, LineAreaChart, Histogram, Gauge, BarList, chart } from '../../campaign-os';
import { usePageChrome } from '../PageChrome';
import { campaignService } from '../../../../services/apiService';
import { useApiData } from '../data/useApiData';
import { ChannelReach } from '../components/ChannelReach';
import { totalReach } from '../data/channelChart';

/**
 * Analytics — wired to GET /campaigns/analytics (computed from real delivery events).
 *
 * Summary tiles + bento grid: engagement trend (delivery / open / click rate by month),
 * delivery-health gauge, open-latency histogram, top campaigns by open rate, geo distribution.
 * Every figure comes from the API response or is derived from it; nothing is hard-coded.
 */
const ZERO_SUMMARY = {
  campaigns: 0,
  recipients: 0,
  avg_delivery_rate: 0,
  avg_open_rate: 0,
  avg_click_rate: 0,
  bounce_rate: 0,
  median_open_latency_hours: null,
  opens_measured: 0,
};
const LATENCY_BUCKETS = ['<1h', '1–6h', '6–24h', '1–3d', '3–7d', '7d+'];

const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
const round1 = (n) => Math.round(Number(n || 0) * 10) / 10;

/** The screen re-reads itself on this cadence; delivery events arrive from the provider webhook. */
const LIVE_REFRESH_MS = 60_000;
const clock = (d) => d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

export function AnalyticsPage() {
  const a = useApiData(() => campaignService.analytics(), [], { cacheKey: 'campaign:analytics' });
  const [updatedAt, setUpdatedAt] = useState(() => new Date());

  // Live: new sends and freshly reconciled delivery events flow in without a reload.
  const refresh = useCallback(() => {
    a.refetch();
    setUpdatedAt(new Date());
  }, [a.refetch]);
  useEffect(() => {
    const t = setInterval(refresh, LIVE_REFRESH_MS);
    return () => clearInterval(t);
  }, [refresh]);

  usePageChrome(
    () => ({
      title: 'Analytics',
      subtitle: `Outreach performance across the vendor base · live delivery events · updated ${clock(updatedAt)}`,
      actions: (
        <Button variant="secondary" icon="refresh" onClick={refresh}>
          Refresh
        </Button>
      ),
    }),
    [refresh, updatedAt]
  );

  // ---- Normalise the payload (zero-filled — never fabricate) ----
  const d = a.data?.data || null;
  const summary = { ...ZERO_SUMMARY, ...(d?.summary || {}) };
  const engagement = Array.isArray(d?.engagement) ? d.engagement : [];
  const topCampaigns = Array.isArray(d?.top_campaigns) ? d.top_campaigns : [];
  const geo = Array.isArray(d?.geo) ? d.geo : [];
  // Country lives on the vendor record, so geo can only speak for recipients matched to
  // one — fewer than the headline count whenever a copy went to somebody unregistered.
  const geoMatched = Number(d?.geo_matched || 0);
  const channels = Array.isArray(d?.channels) ? d.channels : [];
  const channelReach = totalReach(channels);
  const hasData = d?.has_data === true;

  const latency = useMemo(() => {
    if (Array.isArray(d?.latency) && d.latency.length > 0) return d.latency;
    return LATENCY_BUCKETS.map((bucket) => ({ bucket, count: 0 }));
  }, [d]);

  const latencyHighlight = useMemo(() => {
    let best = -1;
    let bestCount = 0;
    latency.forEach((b, i) => {
      if ((b.count || 0) > bestCount) {
        bestCount = b.count;
        best = i;
      }
    });
    return best >= 0 ? best : undefined;
  }, [latency]);

  const openTrend = useMemo(() => monthOverMonth(engagement, 'open_rate'), [engagement]);

  const delivery = round1(summary.avg_delivery_rate);
  const bounce = round1(summary.bounce_rate);
  const deliveryBreakdown = {
    positive: delivery,
    neutral: round1(Math.max(0, 100 - delivery - bounce)),
    negative: bounce,
  };

  const tiles = [
    {
      label: 'Avg open rate',
      value: `${summary.avg_open_rate}%`,
      icon: 'drafts',
      caption: `${fmt(summary.opens_measured)} opens measured`,
    },
    {
      label: 'Avg delivery rate',
      value: `${summary.avg_delivery_rate}%`,
      icon: 'mark_email_read',
      caption: `${fmt(summary.recipients)} recipients`,
    },
    {
      label: 'Median open latency',
      value: formatLatency(summary.median_open_latency_hours),
      icon: 'schedule',
      caption:
        summary.median_open_latency_hours === null || summary.median_open_latency_hours === undefined
          ? 'No opens measured yet'
          : 'Send → first open',
    },
    {
      label: 'Bounce rate',
      value: `${summary.bounce_rate}%`,
      icon: 'report',
      caption: `across ${fmt(summary.campaigns)} campaigns`,
    },
  ];

  const performanceItems = useMemo(
    () =>
      topCampaigns.map((c) => ({
        label: `${c.type === 'REGULARIZATION' ? 'Regularization' : c.subject || 'Campaign'} #${c.id}`,
        value: c.open,
        meta: `${fmt(c.recipients)} recipients`,
      })),
    [topCampaigns]
  );

  const geoItems = useMemo(
    () => geo.map((g) => ({ label: g.country, value: g.pct, meta: `${fmt(g.count)} recipients` })),
    [geo]
  );

  if (a.loading && !a.data) return <AnalyticsSkeleton />;
  if (a.error && !a.data) return <ErrorCard message={a.error} onRetry={a.refetch} />;

  return (
    <div className="space-y-5">
      {a.error && <ErrorCard message={a.error} onRetry={a.refetch} />}

      {!hasData && (
        <EmptyState
          icon="monitoring"
          title="No campaign activity yet"
          text="Analytics are computed from real delivery events. They will populate after the first bulk send."
        />
      )}

      {/* Summary tiles */}
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        {tiles.map((t) => (
          <div key={t.label} className="glass-card glass-interactive p-4">
            <div className="grid h-9 w-9 place-items-center rounded-xl bg-cos-teal-faint text-cos-teal-deep">
              <Icon name={t.icon} className="text-[19px]" weight={500} />
            </div>
            <div className="mono mt-3 text-2xl font-bold leading-none text-cos-ink">{t.value}</div>
            <div className="mt-1">
              <span className="text-2xs font-medium uppercase tracking-wide text-cos-ink-muted">{t.label}</span>
            </div>
            <p className="mt-2 text-[12px] leading-tight text-cos-ink-secondary">{t.caption}</p>
          </div>
        ))}
      </div>

      {/* Bento grid */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        <GlassCard accentRail className="relative lg:col-span-8">
          <CardHeader
            title="Engagement Trend"
            subtitle="Delivery, open and click rate by month (%)"
            action={<TrendPill delta={openTrend} />}
          />
          <LineAreaChart
            data={engagement}
            xKey="label"
            height={244}
            yFormatter={(v) => `${v}%`}
            yWidth={34}
            series={[
              { key: 'delivery_rate', name: 'Delivery', color: chart.bronze, area: true },
              { key: 'open_rate', name: 'Open', color: chart.teal, area: true },
              { key: 'click_rate', name: 'Click', color: chart.blueTeal, area: false },
            ]}
          />
        </GlassCard>

        <GlassCard className="lg:col-span-4">
          <CardHeader title="Delivery Health" subtitle="Share of recipients reached" />
          <div className="flex flex-col items-center pt-2">
            <Gauge value={delivery} label="Delivered" breakdown={deliveryBreakdown} />
            <p className="mt-2 text-center text-2xs leading-tight text-cos-ink-muted">
              Positive = delivered · Neutral = pending / untracked · Negative = bounced
            </p>
          </div>
        </GlassCard>

        {/* Reach by address field — the portfolio's copies, counted like any other row */}
        {channels.length > 0 && (
          <GlassCard className="lg:col-span-12">
            <CardHeader
              title="Reach by address field"
              subtitle="To, CC and BCC across every campaign"
              action={
                <span className="text-2xs text-cos-ink-secondary">
                  <span className="mono font-bold text-cos-ink">{fmt(channelReach)}</span> addresses in all
                </span>
              }
            />
            <ChannelReach channels={channels} split />
          </GlassCard>
        )}

        <GlassCard className="lg:col-span-5">
          <CardHeader title="Open Latency" subtitle="Time from send to first open" />
          <Histogram data={latency} height={216} highlightIndex={latencyHighlight} />
        </GlassCard>

        <GlassCard className="lg:col-span-4">
          <CardHeader title="Campaign Performance" subtitle="Open rate by campaign" />
          <div className="pt-1">
            {performanceItems.length > 0 ? (
              <BarList items={performanceItems} suffix="%" />
            ) : (
              <ListNote>No campaigns with sends yet.</ListNote>
            )}
          </div>
        </GlassCard>

        <GlassCard className="lg:col-span-3">
          <CardHeader
            title="Geo Distribution"
            subtitle={
              geoMatched > 0 && channelReach > geoMatched
                ? `${fmt(geoMatched)} of ${fmt(channelReach)} recipients matched to a vendor`
                : 'Recipients by country'
            }
          />
          <div className="pt-1">
            {geoItems.length > 0 ? (
              <BarList items={geoItems} suffix="%" color={chart.tealDeep} />
            ) : (
              <ListNote>No recipients yet.</ListNote>
            )}
          </div>
        </GlassCard>
      </div>
    </div>
  );
}

export default AnalyticsPage;

// ---------------------------------------------------------------------------------------------
// Local helpers (presentational / pure)
// ---------------------------------------------------------------------------------------------

/** Hours → "42 min" / "3.5 h" / "2.1 d"; null/undefined → "—". */
function formatLatency(hours) {
  if (hours === null || hours === undefined) return '—';
  const h = Number(hours);
  if (Number.isNaN(h)) return '—';
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 48) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} d`;
}

/** Percentage-point change of `key` between the last two points; null when not computable. */
function monthOverMonth(series, key) {
  if (!Array.isArray(series) || series.length < 2) return null;
  const prev = Number(series[series.length - 2]?.[key] || 0);
  const last = Number(series[series.length - 1]?.[key] || 0);
  if (prev <= 0) return null;
  return last - prev;
}

function TrendPill({ delta }) {
  if (delta === null || delta === undefined || Number.isNaN(delta)) return null;
  const up = delta >= 0;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-2xs font-semibold ${
        up ? 'bg-cos-teal-faint text-cos-teal-deep' : 'bg-cos-status-coral-bg text-cos-status-coral'
      }`}
      title="Open rate, latest month vs. previous month"
    >
      <Icon name={up ? 'trending_up' : 'trending_down'} className="text-[14px]" />
      {up ? '+' : ''}
      {delta.toFixed(1)} pts MoM
    </span>
  );
}

function ListNote({ children }) {
  return <p className="py-6 text-center text-[13px] text-cos-ink-muted">{children}</p>;
}

function EmptyState({ icon, title, text }) {
  return (
    <GlassCard className="flex flex-col items-center gap-4 text-center sm:flex-row sm:text-left">
      <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-cos-teal-faint text-cos-teal-deep">
        <Icon name={icon} className="text-[26px]" weight={500} />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="text-[15px] font-bold leading-tight tracking-tight text-cos-ink">{title}</h3>
        <p className="mt-1 text-[13px] leading-snug text-cos-ink-secondary">{text}</p>
      </div>
    </GlassCard>
  );
}

function ErrorCard({ message, onRetry }) {
  return (
    <GlassCard className="flex flex-col items-center gap-4 text-center sm:flex-row sm:text-left">
      <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-cos-status-coral-bg text-cos-status-coral">
        <Icon name="error" className="text-[26px]" weight={500} />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="text-[15px] font-bold leading-tight tracking-tight text-cos-ink">Couldn&apos;t load analytics</h3>
        <p className="mt-1 text-[13px] leading-snug text-cos-ink-secondary">{message}</p>
      </div>
      <Button variant="secondary" icon="refresh" onClick={onRetry}>
        Retry
      </Button>
    </GlassCard>
  );
}

function AnalyticsSkeleton() {
  return (
    <div className="animate-pulse space-y-5" aria-busy="true" aria-label="Loading analytics">
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="glass-card h-[128px]" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        <div className="glass-card h-[320px] lg:col-span-8" />
        <div className="glass-card h-[320px] lg:col-span-4" />
        <div className="glass-card h-[290px] lg:col-span-5" />
        <div className="glass-card h-[290px] lg:col-span-4" />
        <div className="glass-card h-[290px] lg:col-span-3" />
      </div>
    </div>
  );
}
